import { formatDistance, subSeconds } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { ActivityIndicator, Seo } from '../components'
import Data from '../components/aivy-admin/data'
import Logs from '../components/aivy-admin/logs'
import Feedback from '../components/feedback'
import Button from '../components/tailwind/Button'
import PageHeading from '../components/tailwind/PageHeading'
import Analyse from '../components/team-analyse/analyse'
import Intro from '../components/team-analyse/intro'
import Spaces from '../components/team-analyse/spaces'
import { useLanguageContext, useUserContext } from '../context'
import { useCareersQuery } from '../hooks/use-careers-query'
import { useTeamAnalyseQuery } from '../hooks/use-team-analyse-query'

const TeamAnalyse = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { isAdmin } = useUserContext()

  const [selectedCareer, setSelectedCareer] = useState(undefined)

  const [displayLogs, setDisplayLogs] = useState(false)
  const [displayData, setDisplayData] = useState(false)

  const {
    data: teamAnalyse,
    status: teamAnalyseStatus,
    dataUpdatedAt,
    fetchStatus
  } = useTeamAnalyseQuery({
    id
  })

  const { data: careers, status: careersStatus } = useCareersQuery({
    select: (items) => {
      return items
        .filter(({ status }) => status === 'ACTIVE')
        .filter(({ id }) => !id.includes('PRODUCT_TOUR'))
        .filter(({ archived }) => !archived)
    }
  })

  if ([teamAnalyseStatus, careersStatus].includes('pending')) {
    return (
      <div className='mx-12'>
        <ActivityIndicator />
      </div>
    )
  }

  const getFetchingSubtitle = () => {
    if (teamAnalyseStatus === 'error') return null

    // data updated at defaults to 0 for an empty cache
    if (!dataUpdatedAt) {
      return t('team_analyse.fetching_subtitle.is_loading')
    }

    if (fetchStatus === 'fetching') {
      return t('team_analyse.fetching_subtitle.is_updating')
    }

    return t('team_analyse.fetching_subtitle.last_updated_at', {
      distance_sub_seconds: formatDistance(
        subSeconds(new Date(dataUpdatedAt), 3),
        new Date(),
        { locale: { de, en }[language] }
      )
    })
  }

  return (
    <>
      <Seo
        title={`${t('team_analyse.analyse.title')} - ${teamAnalyse.analyse.title}`}
      />
      <PageHeading
        title={teamAnalyse.analyse.title}
        subtitle={getFetchingSubtitle()}
        withActions={
          isAdmin ? (
            <div className='ml-auto flex gap-x-1.5'>
              <Button.SecondaryLG
                text='Show Partner'
                onClick={() => setDisplayData(true)}
              />
              <Button.SecondaryLG
                text='Show Logs'
                onClick={() => setDisplayLogs(true)}
              />
            </div>
          ) : undefined
        }
      />
      <div className='max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8'>
        <div className='mt-8'>
          <Spaces spaces={teamAnalyse.spaces} />
        </div>

        <div className='mt-14'>
          <Analyse
            spaces={teamAnalyse.spaces}
            career={selectedCareer}
            setSelectedCareer={setSelectedCareer}
            careers={careers}
            id={id}
          />
        </div>
      </div>
      <div className='h-16' />
      <Feedback context='TEAM_ANALYSE' />
      <div className='h-32' />
      <Intro />

      <Logs
        title='TEAM-ANALYSE-LOGS'
        open={displayLogs}
        setOpen={setDisplayLogs}
        // TODO
      />
      <Data
        title='TEAM-ANALYSE'
        open={displayData}
        setOpen={setDisplayData}
        data={teamAnalyse}
      />
    </>
  )
}

export default TeamAnalyse
